import React, { useState, useEffect, useMemo } from 'react';
import { Vehicule, Intervention } from '../../../../shared/types/game';
import { VehiculeConfig, FonctionArmement, Competence, PersonnelConfig } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { getServiceIdFromBuildingType } from '../../utils/buildingServiceMapping';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { updateUser } from '../../../../core/store/slices/userSlice';
import VehiculeAddModal from './VehiculeAddModal';
import VehiculeDetailsModal from './VehiculeDetailsModal';
import VehiculeCard from './VehiculeCard';
import VehiculesPaginationBar from './VehiculesPaginationBar';
import VehiculesFiltersBar from './VehiculesFiltersBar';
import { loadVehiculeDetails } from './loadVehiculeDetails';

interface BuildingVehiculesTabProps {
  vehicules: Vehicule[];
  loadingVehicules: boolean;
  buildingType: string;
  buildingId: number;
  userId: number;
  buildingCapacity?: number;
  onVehiculeAdded?: () => void;
}

const BuildingVehiculesTab: React.FC<BuildingVehiculesTabProps> = ({
  vehicules,
  loadingVehicules,
  buildingType,
  buildingId,
  userId,
  buildingCapacity = 0,
  onVehiculeAdded
}) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [vehiculeConfigs, setVehiculeConfigs] = useState<VehiculeConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [showVehiculeDetailsModal, setShowVehiculeDetailsModal] = useState(false);
  const [selectedVehicule, setSelectedVehicule] = useState<Vehicule | null>(null);
  const [fonctionsArmement, setFonctionsArmement] = useState<FonctionArmement[]>([]);
  const [competences, setCompetences] = useState<Competence[]>([]);
  const [personnelConfigs, setPersonnelConfigs] = useState<PersonnelConfig[]>([]);
  const [vehiculeConfigDetails, setVehiculeConfigDetails] = useState<VehiculeConfig | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [vehiculeSearchQuery, setVehiculeSearchQuery] = useState('');
  const [vehiculeFilterStatut, setVehiculeFilterStatut] = useState<string>('all'); // 'all', 'disponible', 'en_mission', 'en_maintenance', 'panne'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('compact');
  const [interventions, setInterventions] = useState<Intervention[]>([]);

  // Obtenir l'ID du service correspondant au type de bâtiment
  const serviceId = getServiceIdFromBuildingType(buildingType);

  // Charger les interventions pour calculer le nombre d'interventions par véhicule
  useEffect(() => {
    const loadInterventions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const response = await fetch(`${getApiBaseUrl(true)}/buildings/interventions`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            setInterventions(data.data);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des interventions:', error);
      }
    };

    loadInterventions();
  }, []);

  // Calculer le nombre d'interventions par véhicule
  const interventionsParVehicule = useMemo(() => {
    const countMap = new Map<number, number>();
    
    interventions.forEach((intervention) => {
      if (intervention.vehicules_ids && Array.isArray(intervention.vehicules_ids)) {
        intervention.vehicules_ids.forEach((vehiculeId) => {
          const currentCount = countMap.get(vehiculeId) || 0;
          countMap.set(vehiculeId, currentCount + 1);
        });
      }
    });
    
    return countMap;
  }, [interventions]);

  // Filtrer les véhicules avec useMemo pour optimiser les performances
  const filteredVehicules = useMemo(() => {
    return vehicules.filter((v) => {
      // Filtre par recherche (nom, type)
      const searchLower = vehiculeSearchQuery.toLowerCase().trim();
      if (searchLower && !(
        v.nom.toLowerCase().includes(searchLower) ||
        v.type.toLowerCase().includes(searchLower)
      )) {
        return false;
      }

      // Filtre par statut
      if (vehiculeFilterStatut !== 'all' && v.statut !== vehiculeFilterStatut) {
        return false;
      }

      return true;
    });
  }, [vehicules, vehiculeSearchQuery, vehiculeFilterStatut]);

  // Pagination
  const totalPages = Math.ceil(filteredVehicules.length / itemsPerPage);
  const paginatedVehicules = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredVehicules.slice(startIndex, endIndex);
  }, [filteredVehicules, currentPage, itemsPerPage]);

  // Réinitialiser la page quand le filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [vehiculeSearchQuery, vehiculeFilterStatut]);

  // Charger les configurations de véhicules filtrées par service
  useEffect(() => {
    const loadVehiculeConfigs = async () => {
      if (!serviceId) return;

      setLoadingConfigs(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${getApiBaseUrl(true)}/config/vehicules`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            // Filtrer par service
            const filteredConfigs = data.data.filter((config: VehiculeConfig) => 
              config.id_service === serviceId
            );
            setVehiculeConfigs(filteredConfigs);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des configurations de véhicules:', error);
      } finally {
        setLoadingConfigs(false);
      }
    };

    // Charger les configurations au montage du composant pour afficher les capacités
    loadVehiculeConfigs();
  }, [serviceId]);

  const handleOpenVehiculeDetails = async (vehicule: Vehicule) => {
    setSelectedVehicule(vehicule);
    setShowVehiculeDetailsModal(true);
    setLoadingDetails(true);
    setVehiculeConfigDetails(null);
    setPersonnelConfigs([]);

    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingDetails(false);
      return;
    }

    try {
      const r = await loadVehiculeDetails(vehicule, token);
      setVehiculeConfigDetails(r.vehiculeConfigDetails);
      setFonctionsArmement(r.fonctionsArmement);
      setCompetences(r.competences);
      setPersonnelConfigs(r.personnelConfigs);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveNom = async (nom: string) => {
    if (!selectedVehicule) {
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${selectedVehicule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ nom })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mettre à jour le véhicule dans la liste locale
        setSelectedVehicule({ ...selectedVehicule, nom });
        // Recharger la liste des véhicules
        if (onVehiculeAdded) {
          onVehiculeAdded();
        }
      } else {
        alert(data.error || 'Erreur lors de la sauvegarde du nom');
        throw new Error(data.error || 'Erreur lors de la sauvegarde du nom');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du nom:', error);
      alert('Erreur lors de la sauvegarde du nom');
      throw error;
    }
  };

  const handleDeleteVehicule = async (vehiculeId: number) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Session expirée. Reconnectez-vous.');
      return;
    }
    const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${vehiculeId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      alert((data as { error?: string }).error || 'Suppression impossible.');
      return;
    }
    if (onVehiculeAdded) {
      onVehiculeAdded();
    }
  };

  const getVehiculeConfig = (vehicule: Vehicule): VehiculeConfig | null => {
    return (
      vehiculeConfigs.find(
        (config) =>
          vehicule.config_vehicle_id != null &&
          config.id != null &&
          config.id === vehicule.config_vehicle_id
      ) ||
      vehiculeConfigs.find(
        (config) => config.nom === vehicule.nom || config.type === vehicule.type
      ) ||
      null
    );
  };

  // VehiculeCard component extracted to ./VehiculeCard.tsx


  // Fonction pour créer un ou plusieurs véhicules
  const handleCreateVehicule = async (config: VehiculeConfig, quantity: number = 1) => {
    if (!currentUser || isCreating) return;

    setIsCreating(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setCreateError('Non authentifié');
        return;
      }

      const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          configId: config.id,
          batimentId: buildingId,
          quantity,
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setCreateError(data.error || 'Erreur lors de la création du véhicule');
        return;
      }

      if (data.data && data.data.newMoney !== undefined && currentUser) {
        dispatch(updateUser({ argents: data.data.newMoney }));
      }

      const count = data.data?.count ?? quantity;
      setCreateSuccess(data.message || `${count} véhicule${count > 1 ? 's' : ''} créé${count > 1 ? 's' : ''} avec succès`);
      onVehiculeAdded?.();
      setTimeout(() => {
        setCreateSuccess(null);
      }, 2000);
    } catch (error: any) {
      console.error('Erreur lors de la création du véhicule:', error);
      setCreateError('Erreur réseau lors de la création du véhicule');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Véhicules ({filteredVehicules.length}{filteredVehicules.length !== vehicules.length ? ` / ${vehicules.length}` : ''})
        </h3>
        <button 
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          + Ajouter
        </button>
      </div>

      {loadingVehicules ? (
        <div className="text-center py-12 text-zinc-400">
          Chargement des véhicules...
        </div>
      ) : vehicules.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <p className="text-lg mb-2">Aucun véhicule assigné</p>
          <p className="text-sm">Cliquez sur "Ajouter" pour assigner un véhicule à ce bâtiment</p>
        </div>
      ) : (
        <>
          <VehiculesFiltersBar
            searchQuery={vehiculeSearchQuery}
            onSearchChange={setVehiculeSearchQuery}
            filterStatut={vehiculeFilterStatut}
            onFilterStatutChange={setVehiculeFilterStatut}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          {/* Liste des véhicules avec hauteur fixe et scroll */}
          <div className="max-h-[500px] overflow-y-auto pr-2">
            {filteredVehicules.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <p className="text-lg mb-2">Aucun résultat trouvé</p>
                <p className="text-sm">Essayez de modifier vos filtres de recherche</p>
              </div>
            ) : (
              <div className={`grid ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' : 'grid-cols-1 gap-3'}`}>
                {paginatedVehicules.map((v) => (
                  <VehiculeCard 
                    key={v.id} 
                    v={v} 
                    compact={viewMode === 'compact'} 
                    onOpenDetails={handleOpenVehiculeDetails}
                    nbInterventions={interventionsParVehicule.get(v.id) || 0}
                    config={getVehiculeConfig(v)}
                  />
                ))}
              </div>
            )}
          </div>

          <VehiculesPaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            resultsCount={filteredVehicules.length}
            onPageChange={setCurrentPage}
          />

        </>
      )}

      {/* Modal d'ajout de véhicule */}
      <VehiculeAddModal
        show={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSearchQuery('');
        }}
        vehiculeConfigs={vehiculeConfigs}
        loadingConfigs={loadingConfigs}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isCreating={isCreating}
        onCreateVehicule={handleCreateVehicule}
        createError={createError}
        createSuccess={createSuccess}
        availableSlots={buildingCapacity > 0 ? Math.max(0, buildingCapacity - vehicules.length) : 99}
      />

      {/* Modal de détails du véhicule */}
      <VehiculeDetailsModal
        show={showVehiculeDetailsModal}
        vehicule={selectedVehicule}
        vehiculeConfigDetails={vehiculeConfigDetails}
        fonctionsArmement={fonctionsArmement}
        competences={competences}
        personnelGradeConfigs={personnelConfigs}
        loadingDetails={loadingDetails}
        onClose={() => {
          setShowVehiculeDetailsModal(false);
          setSelectedVehicule(null);
        }}
        onSaveNom={handleSaveNom}
        onDeleteVehicule={handleDeleteVehicule}
      />
    </div>
  );
};

export default BuildingVehiculesTab;

